import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useCreateTaxRate } from "./use-create-tax-rate";
import {
  TAX_RATE_DESCRIPTION_MAX_LENGTH,
  TAX_RATE_DISPLAY_NAME_MAX_LENGTH,
  TAX_RATE_JURISDICTION_MAX_LENGTH,
} from "./constants";

interface CreateTaxRateDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function CreateTaxRateDialog({ open, onOpenChange, onSuccess }: CreateTaxRateDialogProps) {
  const { t } = useTranslation();
  const create = useCreateTaxRate();
  const [displayName, setDisplayName] = useState("");
  const [percentageInput, setPercentageInput] = useState("");
  const [inclusive, setInclusive] = useState(false);
  const [jurisdiction, setJurisdiction] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDisplayName("");
      setPercentageInput("");
      setInclusive(false);
      setJurisdiction("");
      setDescription("");
      setError(null);
    }
  }, [open]);

  const trimmedDisplayName = displayName.trim();
  const trimmedJurisdiction = jurisdiction.trim();
  const trimmedDescription = description.trim();

  const parsedPercentage = Number.parseFloat(percentageInput);
  const percentageValid =
    Number.isFinite(parsedPercentage) && parsedPercentage >= 0 && parsedPercentage <= 100;

  const isValid =
    trimmedDisplayName.length > 0 &&
    trimmedDisplayName.length <= TAX_RATE_DISPLAY_NAME_MAX_LENGTH &&
    trimmedJurisdiction.length <= TAX_RATE_JURISDICTION_MAX_LENGTH &&
    trimmedDescription.length <= TAX_RATE_DESCRIPTION_MAX_LENGTH &&
    percentageValid;

  const handleClose = (next: boolean) => {
    if (!next && create.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!isValid) return;
    setError(null);
    create.mutate(
      {
        displayName: trimmedDisplayName,
        percentage: parsedPercentage,
        inclusive,
        ...(trimmedJurisdiction.length > 0 ? { jurisdiction: trimmedJurisdiction } : {}),
        ...(trimmedDescription.length > 0 ? { description: trimmedDescription } : {}),
      },
      {
        onSuccess: () => onSuccess(),
        onError: (err) => {
          setError(
            resolveMutationErrorMessage(err, t, {
              forbiddenKey: "catalog.errors.forbidden",
              serverPrefixKey: "catalog.errors.serverPrefix",
              genericKey: "catalog.taxRates.create.error",
            }),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="create-tax-rate-dialog">
        <DialogHeader>
          <DialogTitle>{t("catalog.taxRates.create.title")}</DialogTitle>
          <DialogDescription>{t("catalog.taxRates.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <label
              htmlFor="create-tax-rate-name"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("catalog.taxRates.fields.displayName")}
            </label>
            <Input
              id="create-tax-rate-name"
              data-testid="create-tax-rate-name-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={TAX_RATE_DISPLAY_NAME_MAX_LENGTH}
              disabled={create.isPending}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label
                htmlFor="create-tax-rate-percentage"
                className="font-sans text-sm font-medium text-foreground"
              >
                {t("catalog.taxRates.fields.percentage")}
              </label>
              <Input
                id="create-tax-rate-percentage"
                data-testid="create-tax-rate-percentage-input"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                max="100"
                value={percentageInput}
                onChange={(e) => setPercentageInput(e.target.value)}
                disabled={create.isPending}
                placeholder={t("catalog.taxRates.create.percentagePlaceholder")}
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="create-tax-rate-jurisdiction"
                className="font-sans text-sm font-medium text-foreground"
              >
                {t("catalog.taxRates.fields.jurisdiction")}
              </label>
              <Input
                id="create-tax-rate-jurisdiction"
                data-testid="create-tax-rate-jurisdiction-input"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                maxLength={TAX_RATE_JURISDICTION_MAX_LENGTH}
                disabled={create.isPending}
                placeholder={t("catalog.taxRates.create.jurisdictionPlaceholder")}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 font-sans text-sm">
            <input
              type="checkbox"
              data-testid="create-tax-rate-inclusive-input"
              checked={inclusive}
              onChange={(e) => setInclusive(e.target.checked)}
              disabled={create.isPending}
            />
            <span>{t("catalog.taxRates.create.inclusiveLabel")}</span>
          </label>

          <div className="space-y-1.5">
            <label
              htmlFor="create-tax-rate-description"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("catalog.taxRates.fields.description")}
            </label>
            <Textarea
              id="create-tax-rate-description"
              data-testid="create-tax-rate-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={TAX_RATE_DESCRIPTION_MAX_LENGTH}
              rows={2}
              disabled={create.isPending}
            />
          </div>

          {error && (
            <p
              data-testid="create-tax-rate-error"
              role="alert"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={create.isPending}>
            {t("catalog.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-create-tax-rate-button"
            onClick={handleSubmit}
            disabled={!isValid || create.isPending}
          >
            {create.isPending
              ? t("catalog.taxRates.create.pending")
              : t("catalog.taxRates.actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
